import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Supabase'den mevcut session'ı kontrol et
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
        }
      } catch (error) {
        logger.error('Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Auth state değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        return { 
          success: false, 
          message: error.message 
        };
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Login failed:', error);
      return { 
        success: false, 
        message: 'Giriş başarısız' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            username: userData.username
          }
        }
      });
      
      if (error) {
        return { 
          success: false, 
          message: error.message 
        };
      }

      // Yeni kullanıcı için profile oluştur
      if (data.user) {
        
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              first_name: userData.firstName,
              last_name: userData.lastName,
              email: userData.email
            }
          ]);

        if (profileError) {
          logger.error('Profile creation failed:', profileError);
        }
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Registration failed:', error);
      return { 
        success: false, 
        message: 'Kayıt başarısız' 
      };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('Logout failed:', error);
        return { success: false, message: error.message };
      }
      
      // State'i temizle
      setUser(null);
      
      // LocalStorage'ı temizle
      localStorage.removeItem('selectedUserId');
      
      return { success: true };
    } catch (error) {
      logger.error('Logout failed:', error);
      return { success: false, message: 'Çıkış yapılırken hata oluştu' };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      // Auth.users'ı güncelle
      const { data, error } = await supabase.auth.updateUser({
        data: {
          first_name: profileData.firstName,
          last_name: profileData.lastName
        }
      });
      
      if (error) {
        return { 
          success: false, 
          message: error.message 
        };
      }

      // Profiles tablosunu güncelle
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert([
          {
            id: user.id,
            first_name: profileData.firstName,
            last_name: profileData.lastName,
            email: profileData.email
          }
        ]);

      if (profileError) {
        logger.error('Profile table update failed:', profileError);
        // Auth güncellemesi başarılıysa devam et
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Profile update failed:', error);
      return { 
        success: false, 
        message: 'Profil güncellenemedi' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
